/* eslint-disable max-lines */
import {
  ConsoleSqlOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  FilterOutlined,
  FlagOutlined,
  SaveOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { useMutation } from '@apollo/client'
import { faTablePivot } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  FetchKpiChartDisplaySettingPresetsDocument,
  UpsertKpiChartDisplaySettingPresetDocument,
  type ViewFieldsFragment,
} from '@salescore/client-api'
import { CONSTANT, POSTHOG_EVENTS } from '@salescore/client-base'
import {
  canViewKpiPivotConfig,
  canViewKpiPivotRecord,
  canViewKpiPivotSchema,
  type Period,
  type ProgressType,
  useCan,
  useCanForView,
  useClearViewQueryCache,
  useMe,
} from '@salescore/client-common'
import type {
  ChartFormat,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  ViewConfig,
  ViewConfigFilter,
  ViewConfigKpi,
  ViewConfigKpiParameter,
  ViewConfigKpiPivot,
  ViewConfigKpiPreset,
  ViewUiKpi,
  ViewUiKpiPivot,
} from '@salescore/core'
import { isSharedLinkPresetName, stripSharedLinkPresetNamePrefix } from '@salescore/core'
import { ButtonWithTooltip, organizationHasFeature } from '@salescore/frontend-common'
import { App, Button, Dropdown, type DropdownProps, type MenuProps, message, Row, Space, Switch, Tooltip } from 'antd'
import { diff as deepObjectDiff } from 'deep-object-diff'
import { Array } from 'effect'
import { isNotNullish, isNotUndefined } from 'effect/Predicate'
import { t } from 'i18next'
import { type Dispatch, type ReactElement, type SetStateAction, useEffect, useState } from 'react'
import { type SetterOrUpdater, useRecoilState, useRecoilValue } from 'recoil'
import { z } from 'zod'

import type { RSheetColumn, RSheetRecordNode } from '../../../domain/column.ts'
import { useMustacheParameter } from '../../../hooks/useMustacheParameter'
import { usePeriod } from '../../../hooks/usePeriod'
import { useProgress } from '../../../hooks/useProgress'
import { useViewQueryResult } from '../../../hooks/useViewQueryResult'
import {
  aggregationToRows,
  buildPresetInput,
  checkChartDisplaySettingChanges,
  type KpiChartDisplaySettingPresetsState,
  resolveOriginalViewId,
} from '../../../kpi/kpiChartDisplaySettingPresets'
import { kpiParameterAtom, kpiPivotPickedPresetNameAtom } from '../../../recoil/navigation/atoms'
import { useRecordsImpl } from '../../../recoil/records'
import { additionalConfigAtom } from '../../../recoil/records/atoms'
import { useRefetchKpiPivotQueryResult } from '../../../recoil/records/mutations/useRefetchKpiPivotQueryResult'
import { type SalescoreObjectAbility, type ViewAbility, viewsContextAtom } from '../../../recoil/view/atoms'
import { useSearchView } from '../../../recoil/view/selectors/viewsRelatedSelector'
import { configAtom } from '../../../recoil/viewConfig'
import { ShareViewButton } from '../../ShareViewButton'
import { buildViewQueryResultsMetadataModalProperties } from '../ViewQueryResultsMetadataModal'
import { ViewUIErrorDrawer } from '../ViewUIErrorDrawer'
import { DashboardCsvDownloadPopover } from '../ViewUISheetWithNavigation/DashboardCsvDownloadPopover'
import { buildDimensionFiltersModal } from '../ViewUISheetWithNavigation/SheetNavigation/DimensionFiltersModal'
import { ImportSourceRecordsButton } from '../ViewUISheetWithNavigation/SheetNavigation/ImportButton'
import { buildViewDebugModalProperties } from '../ViewUISheetWithNavigation/ViewDebugModal'
import { CompileErrorsButton } from './CompileErrorsButton'
import { getKpiPresetParameterKeysWithValues, useSetPresetToConfig } from './KpiPresetForm'
import { buildKpiPresetFormModalProperties } from './KpiPresetFormModal'
import { KpiUserAppearancePicker } from './KpiUserAppearancePicker'
import { PeriodPicker } from './PeriodPicker'
import { PickersForKpiPivotView } from './PickersForKpiPivotView.tsx'
import { recursiveRemoveEmptyValues } from './recursive'

const viewTypeSchema = z.object({
  diffVisible: z.boolean(),
  totalVisible: z.boolean(),
  goalVisible: z.boolean(),
  perElapsedBusinessDays: z.boolean().optional(),
  colorSchema: z.enum(['normal', 'success-blue']).optional(),
  subtotalOrder: z.enum([`top`, `bottom`]).optional(),
})

type ViewType = z.infer<typeof viewTypeSchema>

const decideIsKpiPivotView = (config: ViewConfig): config is ViewConfigKpiPivot => config.type === 'kpiPivot'

export const KpiPivotNavigation = ({
  component,
  siderVisibility,
  setSiderVisibility,
  columns,
  compiledConfig,
  chartDisplaySettingProps,
  view,
  ability,
  hasChangeToKpiPivotParameter,
  setHasChangeToKpiPivotParameter,
  setHasChangeToViewSchema,
}: {
  component: ViewUiKpiPivot | ViewUiKpi
  siderVisibility?: boolean
  setSiderVisibility?: (x: boolean | ((x: boolean) => boolean)) => void
  columns: Array<RSheetColumn<RSheetRecordNode>>
  compiledConfig: CompiledKpiViewConfig | CompiledKpiPivotViewConfig
  chartDisplaySettingProps?: {
    format: ChartFormat
    kpiChartDisplaySettingPresetsState: KpiChartDisplaySettingPresetsState
    setKpiChartDisplaySettingPresetsState: Dispatch<SetStateAction<KpiChartDisplaySettingPresetsState>>
    viewId: string
  }
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  hasChangeToKpiPivotParameter: boolean
  setHasChangeToKpiPivotParameter: (hasChange: boolean) => void
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const { clearTargetViewQueryCaches } = useClearViewQueryCache()
  const viewsContext = useRecoilValue(viewsContextAtom)
  const progress = useProgress(compiledConfig)
  const [period] = usePeriod()
  const refetchKpiPivotQueryResult = useRefetchKpiPivotQueryResult(compiledConfig, view)
  const config = useRecoilValue(configAtom)
  const isKpiPivotView = decideIsKpiPivotView(config)
  const [errorsDrawerOpen, setErrorsDrawerOpen] = useState(false)
  const canUpdate = useCanForView('update', view)
  const canDisplayConfigEdit = useCanForView('display-config-edit', view)
  const [parameter, setParameter] = useRecoilState(kpiParameterAtom)
  const withDateField =
    component.type === 'KpiSheet' ? component.kpi.withDateField : component.kpis.some((x) => x.withDateField)

  return (
    <Row justify="space-between" className="py-2 pl-2 pr-5" style={{ borderBottom: '1px solid #E1E6E8' }}>
      <Row>
        {isKpiPivotView && (
          <PickersForKpiPivotView
            view={view}
            withDateField={withDateField}
            config={config}
            hasChangeToKpiPivotParameter={hasChangeToKpiPivotParameter}
            setHasChangeToViewSchema={setHasChangeToViewSchema}
          />
        )}
        <PeriodPicker disabled={!withDateField} />
        {setSiderVisibility !== undefined && (
          <>
            {isKpiPivotView ? (
              <>
                <AverageProgress period={period} progress={progress} />
                <ButtonWithTooltip
                  disabled={!canUpdate || !canDisplayConfigEdit}
                  showTooltip={!canUpdate || !canDisplayConfigEdit}
                  tooltipTitle={t(`権限がありません`)}
                  onClick={() => {
                    setSiderVisibility((x) => !x)
                  }}
                  type="text"
                  icon={<FontAwesomeIcon icon={faTablePivot} />}
                >
                  {t(`集計設定`)}
                </ButtonWithTooltip>
              </>
            ) : (
              siderVisibility === false && (
                <Button
                  onClick={() => {
                    setSiderVisibility(true)
                  }}
                  type="text"
                  icon={<FontAwesomeIcon icon={faTablePivot} />}
                >
                  {t(`集計設定`)}
                </Button>
              )
            )}
          </>
        )}
        {isNotUndefined(parameter.dimensionFilterLeafs) && Array.isArrayNonEmpty(parameter.dimensionFilterLeafs) && (
          <DimensionFiltersButton filters={parameter.dimensionFilterLeafs} ability={ability} />
        )}
        <KpiUserAppearancePicker />
        <SettingButton compiledConfig={compiledConfig} view={view} />
        <DashboardCsvDownloadPopover compiledConfig={compiledConfig} view={view} />
        {isKpiPivotView && <KpiPivotShareViewButton parameter={parameter} view={view} />}
        {isKpiPivotView && isNotUndefined(parameter.invisibleKpiIds) && parameter.invisibleKpiIds.length > 0 && (
          <InvisibleKpisButton
            config={config}
            invisibleKpiIds={parameter.invisibleKpiIds}
            setParameter={setParameter}
            ability={ability}
          />
        )}
        <CompileErrorsButton
          onClick={() => {
            setErrorsDrawerOpen(true)
          }}
          compiledConfig={compiledConfig}
          view={view}
        />
        <ViewUIErrorDrawer
          open={errorsDrawerOpen}
          onClose={() => {
            setErrorsDrawerOpen(false)
          }}
          recordSaveErrors={[]} // ダッシュボードではレコードを保存させたりしないため、常に空
          columns={columns}
          compiledConfig={compiledConfig}
          view={view}
          ability={ability}
        />
      </Row>
      <Space>
        <ImportSourceRecordsButton
          buttonType="link"
          onSuccess={() => {
            // 同期後に古いキャッシュを読まないようにするためにApolloのキャッシュをクリアする
            const { isDrillDown, prevView, setIsChangedDrillDownModal } = viewsContext
            const cacheClearViewId = isDrillDown === true ? prevView?.id : view.id
            clearTargetViewQueryCaches({ viewIds: [cacheClearViewId].filter(isNotNullish) })

            if (isDrillDown === true && setIsChangedDrillDownModal !== undefined) {
              setIsChangedDrillDownModal(true)
            }

            refetchKpiPivotQueryResult.refetch()
          }}
          view={view}
        />
        {(isKpiPivotView || config.type === 'kpi') && chartDisplaySettingProps === undefined && (
          <SaveViewButtons
            config={config}
            parameter={parameter}
            setParameter={setParameter}
            view={view}
            hasChangeToKpiPivotParameter={setHasChangeToKpiPivotParameter}
            setHasChangeToViewSchema={setHasChangeToViewSchema}
          />
        )}
        {chartDisplaySettingProps !== undefined && (
          <ChartDisplaySettingSaveButtons {...chartDisplaySettingProps} ability={ability} />
        )}
      </Space>
    </Row>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function DimensionFiltersButton({
  filters,
  ability,
}: {
  filters: ViewConfigFilter[]
  ability: ViewAbility & SalescoreObjectAbility
}) {
  const { modal } = App.useApp()

  return (
    <ButtonWithTooltip
      disabled={!ability.canDisplayConfigEdit}
      showTooltip={!ability.canDisplayConfigEdit}
      tooltipTitle={t(`権限がありません`)}
      type="text"
      icon={<FilterOutlined />}
      onClick={() => {
        modal.confirm(buildDimensionFiltersModal())
      }}
    >
      {t(`絞り込み`)}({filters.length})
    </ButtonWithTooltip>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function InvisibleKpisButton({
  config,
  invisibleKpiIds,
  setParameter,
  ability,
}: {
  config: ViewConfigKpiPivot
  invisibleKpiIds: string[]
  setParameter: SetterOrUpdater<ViewConfigKpiParameter>
  ability: ViewAbility & SalescoreObjectAbility
}) {
  const searchView = useSearchView()
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const invisibleItems: MenuProps['items'] = invisibleKpiIds
    .map((x) => {
      const view = searchView(x)
      if (view === undefined) {
        // eslint-disable-next-line array-callback-return
        return
      }
      return {
        key: x,
        label: view.name,
        icon: <EyeInvisibleOutlined className="text-slate-400" />,
      }
    })
    .filter(isNotNullish)
  const visibleItems: MenuProps['items'] = config.kpis
    .filter((kpi) => !invisibleKpiIds.includes(kpi.viewId))
    .map((kpi) => {
      const view = searchView(kpi.viewId)
      if (view === undefined) {
        // eslint-disable-next-line array-callback-return
        return
      }
      return {
        key: kpi.viewId,
        label: view.name,
        icon: <EyeOutlined />,
      }
    })
    .filter(isNotNullish)
  const items = [...invisibleItems, ...visibleItems]

  const toggleVisibility = ({ key }: { key: string }): void => {
    if (invisibleKpiIds.includes(key)) {
      setParameter((oldValue) => ({
        ...oldValue,
        invisibleKpiIds: oldValue.invisibleKpiIds?.filter((x) => x !== key),
      }))
    } else {
      setParameter((oldValue) => ({
        ...oldValue,
        invisibleKpiIds: oldValue.invisibleKpiIds === undefined ? [key] : [...oldValue.invisibleKpiIds, key],
      }))
    }
  }

  const handleOpenChange: DropdownProps['onOpenChange'] = (nextOpen, info) => {
    if (info.source === 'trigger' || nextOpen) {
      setDropdownOpen(nextOpen)
    }
  }

  return (
    <Dropdown
      menu={{ items, onClick: toggleVisibility }}
      disabled={!ability.canDisplayConfigEdit}
      open={dropdownOpen}
      onOpenChange={handleOpenChange}
    >
      <ButtonWithTooltip
        disabled={!ability.canDisplayConfigEdit}
        showTooltip={!ability.canDisplayConfigEdit}
        tooltipTitle={t(`権限がありません`)}
        icon={<EyeInvisibleOutlined />}
        className="border-yellow-400 text-yellow-500"
      >
        {t(`{{length}}件の非表示KPI`, { length: invisibleKpiIds.length })}
      </ButtonWithTooltip>
    </Dropdown>
  )
}

// 現在の値で、選択中のプリセットを更新

const SaveViewButtons = ({
  config,
  parameter,
  setParameter,
  view,
  hasChangeToKpiPivotParameter,
  setHasChangeToViewSchema,
}: {
  config: ViewConfigKpiPivot | ViewConfigKpi
  parameter: ViewConfigKpiParameter
  setParameter: SetterOrUpdater<ViewConfigKpiParameter>
  view: ViewFieldsFragment
  hasChangeToKpiPivotParameter: (hasChange: boolean) => void
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const { modal } = App.useApp()
  const canUpdate = useCanForView('update', view)
  const isKpiPivotView = config.type === 'kpiPivot'
  const [picked] = useRecoilState(kpiPivotPickedPresetNameAtom)
  const setPreset = useSetPresetToConfig()
  const pickedPreset = config.presets?.find((x) => x.name === picked)
  const hasPickedPreset = pickedPreset !== undefined

  const { goalConfig: savedGoalConfig, ...savedOtherParameter } = pickedPreset?.parameter ?? { goalConfig: undefined }
  const { goalConfig: currentGoalConfig, ...currentOtherParameter } = parameter

  // 差分からundefined/null/空arrayを除外する
  const diff = {
    ...recursiveRemoveEmptyValues(deepObjectDiff(savedOtherParameter, currentOtherParameter)),
    ...recursiveRemoveEmptyValues(deepObjectDiff(currentOtherParameter, savedOtherParameter)),
  }

  // GoalConfigのみ__typenameや含まれるパラメーターに差異があるためidの比較をしている
  const hasChanges = Object.keys(diff).length > 0 || savedGoalConfig?.id !== currentGoalConfig?.id
  if (hasPickedPreset) {
    hasChangeToKpiPivotParameter(hasChanges)
  }
  const hasChangesToPreset = hasPickedPreset && hasChanges
  const canRevert = hasChangesToPreset && canUpdate
  const canSave = ((isKpiPivotView && !hasPickedPreset) || hasChangesToPreset) && canUpdate

  if (isSharedLinkPresetName(pickedPreset?.name)) {
    return <></>
  }
  return (
    <Space>
      {hasChangesToPreset && (
        <ButtonWithTooltip
          tooltipTitle={t(`権限がないため集計設定の変更を破棄できません`)}
          showTooltip={!canUpdate}
          disabled={!canRevert}
          icon={<DeleteOutlined />}
          danger
          onClick={() => {
            setParameter(pickedPreset.parameter)
            hasChangeToKpiPivotParameter(false)
          }}
        >
          {t(`集計設定変更を破棄`)}
        </ButtonWithTooltip>
      )}
      <ButtonWithTooltip
        tooltipTitle={t(`権限がないため保存できません`)}
        showTooltip={!canUpdate}
        icon={<SaveOutlined />}
        disabled={!canSave}
        type="primary"
        onClick={() => {
          if (hasPickedPreset) {
            const m = modal.confirm(
              buildKpiPresetFormModalProperties({
                preset: pickedPreset,
                presets: config.presets,
                // 「集計設定を保存」経由は編集対象プリセットが値を持つキーを初期選択にする (本 MR 前と同じ式)
                initialSelectedKeys: getKpiPresetParameterKeysWithValues(pickedPreset.parameter),
                onFinish: (newPreset: ViewConfigKpiPreset) => {
                  setPreset(newPreset, pickedPreset)
                  setHasChangeToViewSchema(true)
                  void message.success(t('集計プリセットを保存しました'))
                  m.destroy()
                },
              }),
            )
          } else {
            const newPreset = { name: t('デフォルト'), parameter }
            setPreset(newPreset)
            setHasChangeToViewSchema(true)
          }
        }}
      >
        {t(`集計設定を保存`)}
      </ButtonWithTooltip>
    </Space>
  )
}

const ChartDisplaySettingSaveButtons = ({
  format,
  kpiChartDisplaySettingPresetsState,
  setKpiChartDisplaySettingPresetsState,
  viewId,
  ability,
}: {
  format: ChartFormat
  kpiChartDisplaySettingPresetsState: KpiChartDisplaySettingPresetsState
  setKpiChartDisplaySettingPresetsState: Dispatch<SetStateAction<KpiChartDisplaySettingPresetsState>>
  viewId: string
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  const { canUpdate } = ability
  const [, setKpiPivotParameter] = useRecoilState(kpiParameterAtom)
  const viewsContext = useRecoilValue(viewsContextAtom)
  const { organization } = useMe()
  const originalViewId = resolveOriginalViewId(viewId)
  const [upsertPreset] = useMutation(UpsertKpiChartDisplaySettingPresetDocument, {
    refetchQueries: [
      {
        query: FetchKpiChartDisplaySettingPresetsDocument,
        variables: { organizationId: organization.id, viewId: originalViewId },
      },
    ],
  })

  // kpiChartDisplaySettingPresetsStateから差分検知
  const entry = kpiChartDisplaySettingPresetsState[format]
  const { saved } = entry
  const { hasChanges, canSave, canRevert } = checkChartDisplaySettingChanges(entry, canUpdate)

  // viewsContextにグラフ設定の変更フラグを伝達
  useEffect(() => {
    viewsContext.setHasChartDisplaySettingChanges?.(hasChanges)
  }, [hasChanges])

  return (
    <Space>
      {canRevert && saved !== undefined && (
        <ButtonWithTooltip
          tooltipTitle={t(`権限がないため表示設定の変更を破棄できません`)}
          showTooltip={!canUpdate}
          disabled={!canRevert}
          icon={<DeleteOutlined />}
          danger
          onClick={() => {
            const restoredRows = aggregationToRows(saved.aggregation)
            if (restoredRows.length > 0) {
              setKpiPivotParameter((oldValue) => ({
                ...oldValue,
                pivot: {
                  rows: restoredRows,
                  columns: [],
                },
              }))
            }
            // kpiChartDisplaySettingPresetsStateでdesign + aggregationを一括復元
            setKpiChartDisplaySettingPresetsState((old) => ({
              ...old,
              [format]: { ...old[format], design: saved.design, aggregation: saved.aggregation },
            }))
          }}
        >
          {t(`表示設定変更を破棄`)}
        </ButtonWithTooltip>
      )}
      <ButtonWithTooltip
        tooltipTitle={t(`権限がないため保存できません`)}
        showTooltip={!canUpdate}
        icon={<SaveOutlined />}
        disabled={!canSave}
        type="primary"
        onClick={() => {
          // 保存ボタンクリック時点の値をキャプチャ（savedに使う）
          const savedDesign = entry.design
          const savedAggregation = entry.aggregation
          const setting = buildPresetInput(format, savedDesign, savedAggregation)
          void upsertPreset({
            variables: {
              organizationId: organization.id,
              input: { viewId: originalViewId, setting },
            },
            onCompleted: () => {
              // 保存成功後、savedスナップショットを更新
              setKpiChartDisplaySettingPresetsState((old) => ({
                ...old,
                [format]: {
                  ...old[format],
                  saved: { design: savedDesign, aggregation: savedAggregation },
                },
              }))
              void message.success(t(`表示設定を保存しました`))
            },
            onError: () => {
              void message.error(t(`表示設定の保存に失敗しました`))
            },
          })
        }}
      >
        {t(`表示設定を保存`)}
      </ButtonWithTooltip>
    </Space>
  )
}

const SettingButton = ({
  compiledConfig,
  view,
}: {
  compiledConfig: CompiledKpiViewConfig | CompiledKpiPivotViewConfig
  view: ViewFieldsFragment
}): ReactElement => {
  const { modal } = App.useApp()
  const config = useRecoilValue(configAtom)
  const viewQueryResult = useViewQueryResult(view)
  const mustacheParameter = useMustacheParameter()
  const records = useRecordsImpl()
  const me = useMe()
  const additionalConfig = useRecoilValue(additionalConfigAtom)
  const [kpiParameter, setKpiParameter] = useRecoilState(kpiParameterAtom)
  const hasAssignmentHistoryFeature = organizationHasFeature(me.organization, 'enable_group_assignment_history')
  const canManageAssignmentHistory = useCan('manage-group-assignment-history')
  const showHistoricalGroupResolution = hasAssignmentHistoryFeature && canManageAssignmentHistory

  return (
    <Dropdown
      // eslint-disable-next-line sonarjs/deprecation
      overlay={
        <div className="bg-white p-4 shadow" style={{ width: 300 }}>
          {showHistoricalGroupResolution && (
            <div className="mb-3">
              <Row justify="space-between" align="middle">
                <span className="font-bold">{t('過去所属の反映')}</span>
                <Switch
                  size="small"
                  checked={kpiParameter.enableHistoricalGroupResolution === true}
                  onChange={(checked) => {
                    modal.confirm({
                      title: t('過去所属の反映'),
                      content: (
                        <div>
                          {t('設定を変更すると表示される内容が変わる可能性があります。')}
                          <br />
                          {t('挙動をよく理解した上でお使いください。')}
                          <br />
                          {t('変更してよろしいですか？')}
                        </div>
                      ),
                      onOk: () => {
                        setKpiParameter((previous) => ({
                          ...previous,
                          enableHistoricalGroupResolution: checked,
                        }))
                      },
                    })
                  }}
                />
              </Row>
              <div className="mt-1 text-xs text-gray-500">
                {t(
                  '有効化によりユーザーが過去に所属していたグループに合わせて実績が反映されます。ただし表示や動作が重くなる可能性があります。',
                )}
              </div>
            </div>
          )}
          <Row justify="space-between" className="mb-2">
            <Space>
              <ConsoleSqlOutlined />
              {t(`SQL表示`)}
            </Space>
            <Button
              type="text"
              style={{
                color: CONSTANT.colors.primaryColor,
              }}
              disabled={viewQueryResult === undefined}
              onClick={() => {
                modal.confirm(buildViewQueryResultsMetadataModalProperties(compiledConfig, view, undefined))
              }}
            >
              {t(`表示`)}
            </Button>
          </Row>
          {canViewKpiPivotConfig(me) && (
            <Row justify="space-between" className="mb-2">
              <Space className={CONSTANT.colorClasses.ADMIN_TEXT_COLOR}>
                <ConsoleSqlOutlined />
                {t(`設定編集`)}
              </Space>
              <Button
                type="text"
                style={{
                  color: CONSTANT.colors.primaryColor,
                }}
                onClick={() => {
                  const m = modal.confirm(
                    buildViewDebugModalProperties({
                      content: config,
                      onAfterFinish: () => {
                        m.destroy()
                      },
                    }),
                  )
                }}
              >
                {t(`表示`)}
              </Button>
            </Row>
          )}
          {canViewKpiPivotSchema(me) && (
            <Row justify="space-between" className="mb-2">
              <Space className={CONSTANT.colorClasses.ADMIN_TEXT_COLOR}>
                <ConsoleSqlOutlined />
                {t(`スキーマ表示`)}
              </Space>
              <Button
                type="text"
                style={{
                  color: CONSTANT.colors.primaryColor,
                }}
                onClick={() => {
                  const m = modal.confirm(
                    buildViewDebugModalProperties({
                      content: {
                        config,
                        additionalConfig,
                        mustacheParameter,
                        ui: compiledConfig.ui,
                        queries: compiledConfig.queries,
                      },
                      onAfterFinish: () => {
                        m.destroy()
                      },
                    }),
                  )
                }}
              >
                {t(`表示`)}
              </Button>
            </Row>
          )}
          {canViewKpiPivotRecord(me) && (
            <Row justify="space-between" className="mb-2">
              <Space className={CONSTANT.colorClasses.ADMIN_TEXT_COLOR}>
                <ConsoleSqlOutlined />
                {t(`レコード表示`)}
              </Space>
              <Button
                type="text"
                style={{
                  color: CONSTANT.colors.primaryColor,
                }}
                onClick={() => {
                  const m = modal.confirm(
                    buildViewDebugModalProperties({
                      content: {
                        viewQueryResult: {
                          ui: viewQueryResult?.ui,
                        },
                        records,
                      },
                      onAfterFinish: () => {
                        m.destroy()
                      },
                    }),
                  )
                }}
              >
                {t(`表示`)}
              </Button>
            </Row>
          )}
        </div>
      }
      placement="bottomLeft"
      trigger={['hover']}
    >
      <Button type="text" icon={<SettingOutlined />} />
    </Dropdown>
  )
}

/** ダッシュボード (kpiPivot) の共有リンク発行ボタン。 */
const KpiPivotShareViewButton = ({
  parameter,
  view,
}: {
  parameter: ViewConfigKpiParameter
  view: ViewFieldsFragment
}): ReactElement => {
  const [picked] = useRecoilState(kpiPivotPickedPresetNameAtom)
  return (
    <ShareViewButton
      view={view}
      // コメント経由と保存値を揃えるため [共有リンク] prefix を外して素のプリセット名を保存する
      parameter={{ ...parameter, kpiPivotPresetName: stripSharedLinkPresetNamePrefix(picked) }}
      posthogEvent={POSTHOG_EVENTS.click_shareurl_from_dashboard}
    />
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function AverageProgress({
  progress,
  period,
  hasTimeRelatedDimension,
  viewType,
}: {
  progress: ProgressType
  period: Period | undefined
  hasTimeRelatedDimension?: boolean
  viewType?: ViewType
}) {
  if (period === undefined) {
    return <></>
  }

  const translatedPeriodName = t(period.name)
  // 全期間を設定したときなどにbusinessDaysが0。このとき、文言がおかしくなるので出しわけている。
  const title =
    progress.businessDays === 0
      ? t(`目標達成のために必要な進捗率です`)
      : `${t(
          `{{periodName}}の目標達成のために、今日の時点で必要な進捗率です。{{periodName}}は営業日が{{startAt, datestr}}から{{endAt, datestr}}の間に{{businessDays}}日間あり、今日時点で{{elapsedDays}}日間が経過しているため、必要な進捗率は{{elapsedDays}}/{{businessDays}}={{progressPercent}}%です。`,
          {
            periodName: translatedPeriodName,
            startAt: period.startAt,
            endAt: period.endAt,
            businessDays: progress.businessDays,
            elapsedDays: progress.elapsedDays,
            progressPercent: progress.progressPercent,
            // formatParams: {
            //   startAt: { year: 'numeric', month: 'numeric', day: 'numeric' },
            //   endAt:  { year: 'numeric', month: 'numeric', day: 'numeric' },
            // },
          },
        )} ${
          // eslint-disable-next-line sonarjs/no-nested-conditional
          hasTimeRelatedDimension === true
            ? t(`※時間関係の項目を集計項目に選択している場合、必要な進捗率はセルごとに異なります。`)
            : ``
        }`
  return (
    <Tooltip title={title}>
      <div
        className="ml-4 flex"
        // style={{ width: 120 }}
      >
        <span className="text-lg font-bold">
          <FlagOutlined className="mr-2" />
          {progress.progressPercent}%
        </span>
      </div>
    </Tooltip>
  )
}
